import {
  useContext, useEffect, useState, ChangeEvent, ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import { formatDate } from '#common/utils';
import { ADDONS } from '#common/constants/addons';
import { EnsureSubscriptionComponent } from '#bootstrap/utils';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
} from '#components/time-range.utils';
import DropdownFilterBox from '#components/DropdownFilterBox';
import {
  ProductBulkEditBar,
} from '#addons/product-bulk-edit';

import { FeedbackUIContext } from '#context/feedback-ui-context';
import Rating from '@mui/material/Rating';
import { USER_ROLES } from '#common/constants';
import {
  useGetProductsQuery,
  useDeleteProductsMutation,
  useUploadProductsMutation,
  usePatchProductFeaturedMutation,
} from '#services/product';
import URLSearchParamsEx from '#utils/url-ex';
import {
  parseProductsFromCSV,
} from '#common/products-csv';

import {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSelected,
} from './list.slice';

interface ProductListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  pageSize: number;
  vendors: Record<string, unknown>;
  selected: Array<string | number>;
}

interface ProductCollection {
  id: string | number;
  name: string;
}

interface ProductRow {
  id?: string | number;
  name?: string;
  collections?: ProductCollection[];
  inactivated?: boolean;
  featured?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export default function ProductList() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteProducts] = useDeleteProductsMutation();
  const [uploadProducts] = useUploadProductsMutation();
  const [patchProductFeatured] = usePatchProductFeaturedMutation();

  const filter = useSelector((state: { productList: ProductListSlice }) => state.productList.filter);
  const dateRange = useSelector((state: { productList: ProductListSlice }) => state.productList.dateRange);
  const filterKey = useSelector((state: { productList: ProductListSlice }) => state.productList.filterKey);
  const headerFilters = useSelector((state: { productList: ProductListSlice }) => state.productList.headerFilters);
  const order = useSelector((state: { productList: ProductListSlice }) => state.productList.order);
  const orderBy = useSelector((state: { productList: ProductListSlice }) => state.productList.orderBy);
  const pageIndex = useSelector((state: { productList: ProductListSlice }) => state.productList.pageIndex);
  const rows = useSelector((state: { productList: ProductListSlice }) => state.productList.rows);
  const rowCount = useSelector((state: { productList: ProductListSlice }) => state.productList.rowCount);
  const pageSize = useSelector((state: { productList: ProductListSlice }) => state.productList.pageSize);
  const vendors = useSelector((state: { productList: ProductListSlice }) => state.productList.vendors);
  const selected = useSelector((state: { productList: ProductListSlice }) => state.productList.selected);
  const userRole = useSelector((state: { auth: { userInfo?: { role?: string } } }) => state.auth.userInfo?.role);
  const canEditRating = USER_ROLES.ADMIN_AND_STUFF.includes(userRole ?? '');
  const [filterForQuery, setFilterForQuery] = useState(filter);

  const [isInitialMount, setIsInitialMount] = useState(true);

  const dateRangeParam = resolveDateRangeParam('createdAt', dateRange);

  const query = useGetProductsQuery({
    dateRange: dateRangeParam,
    order,
    orderBy,
    filter: filterForQuery,
    filterKey,
    headerFilters,
    pageIndex,
    pageSize,
    all: true,
  }, {
    refetchOnMountOrArgChange: true,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.headerFilters !== undefined) {
      dispatch(setHeaderFilters(urlParams.values.headerFilters as Record<string, Record<string, boolean>>));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as unknown as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'name',
    label: 'Product',
    renderer: (row, idx) => {
      const product = row as ProductRow;
      const productIndex = pageIndex * pageSize + idx + 1;
      const qs = new URLSearchParamsEx({
        order,
        orderBy,
        filterKey,
        filter,
        headerFilters,
        pageSize,
        productId: product.id,
        productIndex,
        // The edit page re-runs the list query to resolve productIndex (and to
        // walk prev/next), so it needs the same date window the list used —
        // without it the index points into the unfiltered list.
        ...(dateRangeParam ? { dateRange: dateRangeParam } : {}),
      });
      const url = `/management/product/edit?${qs.toString()}`;
      return (
        <Box>
          <Link
            component="button"
            onClick={() => {
              void navigate(url);
            }}
          >
            {product.name}
          </Link>
        </Box>
      );
    },
  }, {
    id: 'collectionName',
    label: 'Collection',
    renderer: (row) => (row as ProductRow).collections?.map((c) => {
      const url = `/management/collection/edit?collectionId=${c.id}`;
      return (
        <Chip
          key={c.id}
          size="small"
          label={(
            <Link
              component="button"
              onClick={() => {
                void navigate(url);
              }}
              sx={{
                ml: 0.5,
                textDecoration: 'inherit',
                color: 'inherit',
              }}
            >
              {c.name}
            </Link>
          )}
        />
      );
    }),
  }, {
    id: 'vendor',
    label: 'Vendor',
    filters: vendors as Record<string, ReactNode>,
  }, {
    id: 'inactivated',
    label: 'Status',
    renderer: (row) => ((row as ProductRow).inactivated ? <Chip size="small" label="Inactive" color="warning" /> : <Chip size="small" label="Active" color="success" />),

  }, {
    id: 'featured',
    label: 'Rating',
    renderer: (row) => {
      const product = row as ProductRow;
      return (
        <Rating
          name={`featured-${product.id}`}
          value={product.featured || 0}
          max={5}
          precision={0.5}
          readOnly={!canEditRating}
          onChange={(event, newValue) => {
            patchProductFeatured({ productId: product.id, featured: newValue ?? 0 })
              .unwrap()
              .then(() => { void query.refetch(); })
              .catch((err) => { simpleSnackbar({ error: err }); });
          }}
        />
      );
    },

  }, {
    id: 'createdAt',
    label: 'Created At',
    renderer: (row) => formatDate((row as ProductRow).createdAt as string, '-'),
  }, {
    id: 'updatedAt',
    label: 'Last Updated',
    renderer: (row) => formatDate((row as ProductRow).updatedAt as string, '-'),
  }, {
    id: 'action',
    label: 'Action',
    sortable: false,
    renderer: (row) => {
      const product = row as ProductRow;
      const handleDeleteProduct = () => {
        setDialogData({
          title: 'Delete',
          text: 'Are you sure you want to delete this item?',
          disagree: 'Cancel',
          agree: 'Delete',
          onDisagree: () => hideDialog(),
          onAgree: () => {
            hideDialog();
            showBackdrop();

            void deleteProducts({
              // params for list products after delete
              order,
              orderBy,
              filter,
              pageIndex,
              pageSize,
              all: true,
              // params for delete product
              selected: [product.id],
            }).then((_res) => {
              hideBackdrop();
            });
          },
        });
        showDialog();
      };

      return (
        <Box>
          <Link component="button" onClick={handleDeleteProduct}>Delete</Link>
        </Box>
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'variants',
    pinnedColumns: {
      left: ['name'],
    },
    headers: tableHeaders,

    enableSort: true,

    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    hasSelectColumn: true,
    selected,
    setSelected: setSelected as EnhancedTableProps['setSelected'],

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Created',
      defaultPreset: ALL_TIME_RANGE_PRESET,
      entityLabel: 'products',
    },

    rows,
  };

  const uploadCSV = (evt: ChangeEvent<HTMLInputElement>) => {
    const reader = new FileReader();
    reader.readAsText(evt.target.files![0]!);

    reader.onload = (e) => {
      const parseResult = parseProductsFromCSV((e.target as FileReader).result as string);

      const hasError = simpleSnackbar({
        error: parseResult.error
          ? {
            data: {
              message: parseResult.message,
            },
          }
          : false,
      } as unknown as Parameters<typeof simpleSnackbar>[0]);
      if (hasError) {
        evt.target.value = '';
        return;
      }

      showBackdrop();
      void uploadProducts({
        products: parseResult.data,
        order,
        orderBy,
        filter,
        pageIndex,
        pageSize,
        all: true,
      }).then((res) => {
        simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Products was successfully added.');
      }).finally(() => {
        evt.target.value = '';
        hideBackdrop();
      });
    };
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Products
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <DropdownFilterBox
          width={500}
          dropdownItems={[{
            value: 'name',
            text: 'Name',
          }, {
            value: 'collectionName',
            text: 'Collection',
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder="Filter products"
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />

        <ButtonGroup variant="outlined" size="small">
          <Button component="label">
            Import
            <input
              type="file"
              accept="text/csv"
              hidden
              onChange={uploadCSV}
            />
          </Button>
        </ButtonGroup>
      </Box>

      <EnsureSubscriptionComponent addonId={ADDONS.PRODUCT_BULK_EDIT}>
        <ProductBulkEditBar
          selected={selected}
          misc={{
            order,
            orderBy,
            filter: filterForQuery,
            filterKey,
            headerFilters,
            pageIndex,
            pageSize,
            all: true,
          }}

        />
      </EnsureSubscriptionComponent>

      <EnhancedTable
        {...tableProps}
      />
    </>
  );
}
